import { useEffect, useRef, useState } from 'react';
import { Badge, Button, Spinner } from 'reactstrap';
import { FaCheck, FaEdit, FaFileImport, FaPlus, FaSearch, FaTrash } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { supplierActions } from '../../sagas/supplierSlice';
import Pagination from '../Common/Pagination';
import './FournisseurCompact.css';

const PAGE_SIZE = 10;

const FournisseurList = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { suppliers = [], loading: suppliersLoading, error } = useSelector(state => state.suppliers);
    const searchInputRef = useRef(null);
    const importInputRef = useRef(null);
    const [filter, setFilter] = useState('');
    const [debouncedFilter, setDebouncedFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const totalPages = Math.ceil(suppliers.length / PAGE_SIZE);
    const visibleSuppliers = suppliers.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedFilter(filter);
            setCurrentPage(0);
        }, 300);
        return () => clearTimeout(timer);
    }, [filter]);

    useEffect(() => {
        dispatch(supplierActions.findAll({ query: debouncedFilter }));
    }, [dispatch, debouncedFilter]);

    useEffect(() => {
        const focusSearch = event => {
            if (event.key === 'F2') {
                event.preventDefault();
                searchInputRef.current?.focus();
            }
        };
        window.addEventListener('keydown', focusSearch);
        return () => window.removeEventListener('keydown', focusSearch);
    }, []);

    useEffect(() => {
        setCurrentPage(page => Math.min(page, Math.max(0, totalPages - 1)));
    }, [totalPages]);

    const handleEdit = fournisseur => navigate(`/purchases-module/fournisseurs/${fournisseur.id}/edit`, { state: { fournisseur } });

    const handleDeleteFournisseur = (supId) => {
        Swal.fire({
            title: 'Êtes-vous sûr ?',
            text: "Le fournisseur sera désactivé et ne pourra plus être utilisé.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Oui, désactiver !',
            cancelButtonText: 'Annuler'
        }).then((result) => {
            if (result.isConfirmed) {
                let payload = {
                    id: supId,
                    onSuccess: () => {
                        Swal.fire(
                            'Inactivé !',
                            'Le fournisseur a été désactivé avec succès.',
                            'success'
                        );
                        dispatch(supplierActions.findAll({
                            query: debouncedFilter
                        })); // Recharger la liste des clients après la suppression
                    },
                    onError: () => {
                        Swal.fire(
                            'Erreur !',
                            'Une erreur s\'est produite lors de la désactivation.',
                            'error'
                        );
                    }
                }

                dispatch(supplierActions.inactivate(payload));
            }
        });
    };

    const handleActiverFournisseur = (supId) => {
        Swal.fire({
            title: 'Êtes-vous sûr ?',
            text: "Le fournisseur sera activé et pourra être utilisé.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: 'rgba(6, 167, 60, 1)',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Oui, activer !',
            cancelButtonText: 'Annuler'
        }).then((result) => {
            if (result.isConfirmed) {
                let payload = {
                    id: supId,
                    onSuccess: () => {
                        Swal.fire(
                            'Activé !',
                            'Le fournisseur a été activé avec succès.',
                            'success'
                        );
                        dispatch(supplierActions.findAll({
                            query: debouncedFilter
                        })); // Recharger la liste des clients après la suppression
                    },
                    onError: () => {
                        Swal.fire(
                            'Erreur !',
                            'Une erreur s\'est produite lors de l\'activation.',
                            'error'
                        );
                    }
                }
                dispatch(supplierActions.activate(payload));
            }
        });
    };

    const handleImportClick = () => {
        importInputRef.current?.click();
    };

    const handleImportFile = (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';

        if (!file) return;

        const extension = file.name.split('.').pop()?.toLowerCase();
        if (!['xlsx', 'xls'].includes(extension)) {
            Swal.fire('Format invalide', 'Veuillez importer un fichier Excel .xlsx ou .xls avec les colonnes name et type.', 'warning');
            return;
        }

        dispatch(supplierActions.importSuppliers({
            file,
            onSuccess: (result) => {
                const imported = result?.imported || 0;
                const skipped = result?.skipped || 0;
                const errors = result?.errors || [];

                Swal.fire({
                    icon: imported > 0 ? 'success' : 'warning',
                    title: 'Import fournisseurs termine',
                    text: `${imported} fournisseur(s) importé(s), ${skipped} ligne(s) ignorée(s). ${errors.slice(0, 5).join(' - ')}`
                });

                setCurrentPage(0);
                dispatch(supplierActions.findAll({ query: debouncedFilter }));
            },
            onError: () => {
                Swal.fire('Erreur', "Impossible d'importer le fichier. Verifiez les colonnes name et type.", 'error');
            }
        }));
    };


    return (
        <section className="supplier-compact">
            <header className="supplier-compact-header">
                <h4>Fournisseurs <small className="text-muted">({suppliers.length})</small></h4>
                <div className="supplier-compact-actions">
                    <input type="file" ref={importInputRef} accept=".xlsx,.xls" className="d-none" onChange={handleImportFile} />
                    <Button color="secondary" outline size="sm" onClick={handleImportClick} disabled={suppliersLoading}><FaFileImport aria-hidden="true" /> Importer Excel</Button>
                    <Button color="primary" size="sm" onClick={() => navigate('/purchases-module/fournisseurs/new')}><FaPlus aria-hidden="true" /> Nouveau fournisseur</Button>
                </div>
            </header>
            <div className="supplier-compact-panel">
                <div className="input-group supplier-compact-search">
                    <span className="input-group-text"><FaSearch aria-hidden="true" /></span>
                    <input ref={searchInputRef} type="search" className="form-control" aria-label="Rechercher un fournisseur" placeholder="Nom, ICE / CIN ou téléphone" value={filter} onChange={event => setFilter(event.target.value)} />
                </div>
                {error && <div role="alert" className="alert alert-danger py-2">Impossible de charger les fournisseurs. <Button size="sm" color="link" onClick={() => dispatch(supplierActions.findAll({ query: debouncedFilter }))}>Réessayer</Button></div>}
                {suppliersLoading ? <div className="supplier-compact-loading" role="status"><Spinner size="sm" /> Chargement…</div> : !error && <>
                    <div className="table-responsive">
                        <table className="table table-hover supplier-compact-table">
                            <thead><tr><th scope="col">Nom</th><th scope="col">ICE / CIN</th><th scope="col">Téléphone</th><th scope="col" className="text-end">Actions</th></tr></thead>
                            <tbody>
                                {visibleSuppliers.map(supplier => (
                                    <tr key={supplier.id}>
                                        <td><strong>{(supplier.typeFournisseur === 'ENTREPRISE' ? supplier.entreprise : [supplier.nom, supplier.prenom].filter(Boolean).join(' ')) || supplier.nom || supplier.entreprise || '—'}</strong>{!supplier.active && <Badge color="secondary" className="ms-2">Inactif</Badge>}</td>
                                        <td>{(supplier.typeFournisseur === 'ENTREPRISE' ? supplier.ice || supplier.cin : supplier.cin || supplier.ice) || '—'}</td>
                                        <td>{supplier.telephone1 || '—'}</td>
                                        <td><div className="supplier-compact-row-actions">
                                            <Button color="primary" outline size="sm" onClick={() => handleEdit(supplier)}><FaEdit aria-hidden="true" /> Modifier</Button>
                                            <Button color={supplier.active ? 'danger' : 'success'} outline size="sm" title={supplier.active ? 'Désactiver le fournisseur' : 'Activer le fournisseur'} aria-label={supplier.active ? 'Désactiver le fournisseur' : 'Activer le fournisseur'} onClick={() => supplier.active ? handleDeleteFournisseur(supplier.id) : handleActiverFournisseur(supplier.id)}>{supplier.active ? <FaTrash aria-hidden="true" /> : <FaCheck aria-hidden="true" />}</Button>
                                        </div></td>
                                    </tr>
                                ))}
                                {!suppliers.length && <tr><td colSpan="4" className="text-center text-muted py-3">{filter ? 'Aucun fournisseur trouvé.' : 'Aucun fournisseur. Cliquez sur « Nouveau fournisseur » pour commencer.'}</td></tr>}
                            </tbody>
                        </table>
                    </div>
                    <footer className="supplier-compact-list-footer">
                        <small className="text-muted">{suppliers.length ? `${currentPage * PAGE_SIZE + 1}–${Math.min((currentPage + 1) * PAGE_SIZE, suppliers.length)} sur ${suppliers.length}` : '0 fournisseur'}</small>
                        <Pagination totalPages={totalPages} currentPage={currentPage} onPageChange={setCurrentPage} />
                    </footer>
                </>}
            </div>
        </section>
    );
};

export default FournisseurList;
